'use client';

import React, { useState, useEffect, useMemo } from 'react';
import {
  Mail,
  Phone,
  MessageSquare,
  Clock,
  Send,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Headphones,
  FileText,
  Search,
  PlusCircle,
  RefreshCw,
  Building,
  MapPin,
  ChevronRight,
  Shield,
  Layers,
  Paperclip,
  X,
  Download,
} from 'lucide-react';
import {
  useTenantTickets,
  useTenantTicketDetail,
  useCreateTicket,
  useAddTenantMessage,
  useCreatePublicTicket,
  uploadTenantSupportAttachment,
  downloadTenantSupportAttachment,
  type SupportQueryType,
  type SupportPriority,
  type SupportTicketStatus,
} from '@/hooks/useSupportTickets';
import { useContracts } from '@/hooks/useContracts';
import { toast } from 'sonner';

export const SUPPORT_PUBLIC_CATEGORIES = [
  'Sign-In & Authentication Issues',
  'Sign-Up / Onboarding Issues',
  'Access Permission Issues',
  'Submission Workflow Issues',
  'Documentation & Policy Issues',
  'Equipment Testing Issues',
  'QA / Compliance Checklist Gaps',
  'Dashboard / UI Errors',
  'API / Integration Failures',
  'Notification / Broadcast Delivery Failures',
  'Feature Requests',
  'General Inquiry',
] as const;

interface ContactSupportProps {
  onBack?: () => void;
  onBrowseFAQs?: () => void;
  initialSource?: string;
  initialTab?: 'new' | 'tickets';
}

interface UserAssignmentData {
  platformRole?: string;
  userName?: string;
  userEmail?: string;
  assignments?: {
    contracts?: Array<{ id: string; contractName: string; contractNumber: string }>;
    sites?: Array<{ id: string; siteName: string; siteCode: string; contractId?: string }>;
  };
}

export function ContactSupport({ onBack, onBrowseFAQs, initialSource, initialTab }: ContactSupportProps) {
  // Tab Navigation: 'new' | 'tickets'
  const [activeTab, setActiveTab] = useState<'new' | 'tickets'>(initialTab || 'new');
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);

  // User Assignments & Role from /api/v1/assignments/me
  const [userProfile, setUserProfile] = useState<UserAssignmentData | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);

  // Check if authenticated
  const isAuthenticated = Boolean(userProfile?.userEmail || userProfile?.platformRole || userProfile?.userName);

  // Guest mode override (supports unauthenticated visitors and ?guest=true / ?public=true)
  const [forceGuestMode, setForceGuestMode] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const sp = new URLSearchParams(window.location.search);
      if (sp.get('guest') === 'true' || sp.get('public') === 'true') {
        setForceGuestMode(true);
      }
      const tabParam = sp.get('tab') || sp.get('view');
      if (tabParam === 'tickets') {
        setActiveTab('tickets');
      } else if (tabParam === 'new') {
        setActiveTab('new');
      }
    }
  }, []);

  const isGuest = (!loadingProfile && !isAuthenticated) || forceGuestMode;

  // Dynamic public contact
  const [supportContact, setSupportContact] = useState<{ email: string; phone: string }>({
    email: 'support@mepify.com',
    phone: '+91 1800 123 456',
  });

  // Public submission form state
  const [publicFullName, setPublicFullName] = useState('');
  const [publicEmail, setPublicEmail] = useState('');
  const [publicPhone, setPublicPhone] = useState('');
  const [publicCategory, setPublicCategory] = useState<string>('General Inquiry');
  const [publicSubject, setPublicSubject] = useState('');
  const [publicMessage, setPublicMessage] = useState('');
  const [publicErrors, setPublicErrors] = useState<Record<string, string>>({});
  const [publicSuccessData, setPublicSuccessData] = useState<{
    ticketCode: string;
    email: string;
    subject: string;
    category: string;
  } | null>(null);

  const createPublicTicketMutation = useCreatePublicTicket();

  // Authenticated ticket creation form state
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [contractId, setContractId] = useState<string>('');
  const [projectSiteId, setProjectSiteId] = useState<string>('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [recentlyCreatedCode, setRecentlyCreatedCode] = useState<string | null>(null);

  // Tickets filter & search in "My Tickets"
  const [ticketSearch, setTicketSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  // Reply message draft state
  const [replyText, setReplyText] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const filesArray = Array.from(e.target.files);
      for (const f of filesArray) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`File ${f.name} exceeds 10MB limit.`);
          return;
        }
        if (f.size === 0) {
          toast.error(`File ${f.name} cannot be empty (0 bytes).`);
          return;
        }
      }
      setSelectedFiles((prev) => [...prev, ...filesArray]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // TanStack Query hooks
  const {
    data: tenantTicketsData,
    isLoading: loadingTickets,
    isError: ticketsError,
    refetch: refetchTickets,
  } = useTenantTickets(
    {
      search: ticketSearch || undefined,
      status: statusFilter === 'ALL' ? undefined : (statusFilter as SupportTicketStatus),
    },
    { enabled: !isGuest && isAuthenticated }
  );

  const {
    data: activeTicketDetail,
    isLoading: loadingTicketDetail,
    isError: ticketDetailError,
    refetch: refetchTicketDetail,
  } = useTenantTicketDetail(!isGuest && isAuthenticated ? selectedTicketId : null);

  const createTicketMutation = useCreateTicket();
  const addMessageMutation = useAddTenantMessage();

  // Query active contracts if user has contracts:view (handles 403 gracefully)
  const { contracts: contractsList } = useContracts({ isDemo: false });

  // Load user assignments & permissions safely without forcing 401 signOut on unauthenticated visitors
  useEffect(() => {
    async function loadUserData() {
      // If force guest mode from URL (?guest=true / ?public=true), do not fetch profile
      if (forceGuestMode) {
        setUserProfile(null);
        setLoadingProfile(false);
        return;
      }

      try {
        const contactRes = await fetch('/api/proxy/api/v1/public/support-contact')
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);
        if (contactRes?.email && contactRes?.phone) {
          setSupportContact({ email: contactRes.email, phone: contactRes.phone });
        }
      } catch {
        // use default fallback support contact
      }

      // Safe fetch for assignments using native fetch so 401 never triggers a redirect or logout.
      try {
        const meRes = await fetch('/api/proxy/api/v1/assignments/me')
          .then((r) => (r.ok ? r.json() : null))
          .catch(() => null);
        if (meRes && (meRes.userEmail || meRes.platformRole || meRes.userName)) {
          setUserProfile(meRes);
        } else {
          setUserProfile(null);
        }
      } catch (err) {
        console.error('Failed to load user profile assignments:', err);
        setUserProfile(null);
      } finally {
        setLoadingProfile(false);
      }
    }
    loadUserData();
  }, [forceGuestMode]);

  // Pre-fill category if source specified
  useEffect(() => {
    if (initialSource === 'signin') {
      setCategory('Sign-In & Authentication Issues');
      setPublicCategory('Sign-In & Authentication Issues');
    } else if (initialSource === 'signup') {
      setCategory('Sign-Up / Onboarding Issues');
      setPublicCategory('Sign-Up / Onboarding Issues');
    }
  }, [initialSource]);

  // Sourced valid contracts accessible to the authenticated user
  const availableContracts = useMemo(() => {
    const map = new Map<string, { id: string; contractName: string; contractNumber: string }>();

    for (const c of userProfile?.assignments?.contracts || []) {
      if (c.id) {
        map.set(c.id, {
          id: c.id,
          contractName: c.contractName || 'Contract',
          contractNumber: c.contractNumber || '',
        });
      }
    }

    if (contractsList && contractsList.length > 0) {
      for (const c of contractsList) {
        if (c.id && !map.has(c.id)) {
          map.set(c.id, {
            id: c.id,
            contractName: c.name || 'Contract',
            contractNumber: c.contractNumber || '',
          });
        }
      }
    }

    return Array.from(map.values());
  }, [userProfile, contractsList]);

  // Sourced valid project sites belonging to the selected contract and accessible to the authenticated user
  const availableSites = useMemo(() => {
    if (!contractId) return [];
    const map = new Map<string, { id: string; siteName: string; siteCode: string }>();

    const profileSites = userProfile?.assignments?.sites || [];
    for (const s of profileSites) {
      if (s.contractId === contractId && s.id) {
        map.set(s.id, {
          id: s.id,
          siteName: s.siteName || 'Site',
          siteCode: s.siteCode || '',
        });
      }
    }

    const contractObj = contractsList?.find((c) => c.id === contractId);
    if (contractObj?.linkedProjectSites) {
      for (const ls of contractObj.linkedProjectSites) {
        if (ls.siteId && !map.has(ls.siteId)) {
          map.set(ls.siteId, {
            id: ls.siteId,
            siteName: ls.siteName || ls.siteId,
            siteCode: ls.siteId,
          });
        }
      }
    }

    return Array.from(map.values());
  }, [contractId, userProfile, contractsList]);

  const handleContractChange = (newContractId: string) => {
    setContractId(newContractId);
    // Clear/reset the previously selected Project Site whenever contract changes
    setProjectSiteId('');
  };

  // Validation
  const validateCreateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!category.trim()) {
      errors.category = 'Please select a category';
    }

    if (!subject.trim()) {
      errors.subject = 'Subject is required';
    } else if (subject.trim().length < 3) {
      errors.subject = 'Subject must be at least 3 characters';
    }

    if (!description.trim()) {
      errors.description = 'Description is required';
    } else if (description.trim().length < 5) {
      errors.description = 'Description must be at least 5 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Create Ticket
  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateCreateForm()) return;

    try {
      const createdTicket = await createTicketMutation.mutateAsync({
        category: category.trim(),
        subject: subject.trim(),
        description: description.trim(),
        contractId: contractId || null,
        projectSiteId: projectSiteId || null,
      });

      toast.success('Support ticket created successfully!', {
        description: `Ticket Code: ${createdTicket.ticketCode}`,
      });

      setRecentlyCreatedCode(createdTicket.ticketCode);
      // Reset form
      setSubject('');
      setDescription('');
      setCategory('');
      setContractId('');
      setProjectSiteId('');
      setFormErrors({});

      // Switch to tickets view and select the created ticket
      setSelectedTicketId(createdTicket.id);
      setActiveTab('tickets');
    } catch (err: unknown) {
      const errorObj = err as { data?: { error?: string }; message?: string; status?: number };
      const msg = errorObj?.data?.error || errorObj?.message || 'Failed to submit support ticket';
      toast.error('Submission Failed', { description: msg });
    }
  };

  const handleCancelCreate = () => {
    setSubject('');
    setDescription('');
    setCategory('');
    setContractId('');
    setProjectSiteId('');
    setFormErrors({});
    if (onBack) {
      onBack();
    }
  };

  // Validation for unauthenticated public submissions
  const validatePublicForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!publicFullName.trim()) {
      errors.fullName = 'Name is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!publicEmail.trim()) {
      errors.email = 'Email is required';
    } else if (!emailRegex.test(publicEmail.trim())) {
      errors.email = 'Please enter a valid email address';
    }

    const phoneRegex = /^\+?[\d\s-()]+$/;
    if (!publicPhone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!phoneRegex.test(publicPhone.trim())) {
      errors.phone = 'Please enter a valid phone number';
    }

    if (!publicCategory) {
      errors.category = 'Please select a category';
    }

    if (!publicSubject.trim()) {
      errors.subject = 'Subject is required';
    }

    if (!publicMessage.trim()) {
      errors.message = 'Message is required';
    } else if (publicMessage.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters';
    }

    setPublicErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle Public Ticket Submission
  const handlePublicSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validatePublicForm()) return;

    try {
      const res = await createPublicTicketMutation.mutateAsync({
        fullName: publicFullName.trim(),
        email: publicEmail.trim().toLowerCase(),
        phone: publicPhone.trim(),
        subject: publicSubject.trim(),
        message: publicMessage.trim(),
        category: publicCategory,
      });

      toast.success('Support request submitted!', {
        description: `Reference Code: ${res.ticketCode}`,
      });

      setPublicSuccessData({
        ticketCode: res.ticketCode,
        email: publicEmail.trim(),
        subject: publicSubject.trim(),
        category: publicCategory,
      });
      setPublicErrors({});
    } catch (err: unknown) {
      const errorObj = err as { data?: { error?: string }; message?: string };
      const msg = errorObj?.data?.error || errorObj?.message || 'Failed to submit support request';
      toast.error('Submission Failed', { description: msg });
    }
  };

  // Handle Tenant Message Reply
  const handleSendReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicketId || (!replyText.trim() && selectedFiles.length === 0)) return;

    try {
      setUploadingFiles(true);
      const attachmentFileIds: string[] = [];
      for (const file of selectedFiles) {
        const res = await uploadTenantSupportAttachment(selectedTicketId, file);
        attachmentFileIds.push(res.fileId);
      }

      await addMessageMutation.mutateAsync({
        ticketId: selectedTicketId,
        body: replyText.trim() || 'Attached files',
        attachmentFileIds,
      });
      setReplyText('');
      setSelectedFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast.success('Message sent to support');
    } catch (err: unknown) {
      const errorObj = err as { data?: { error?: string }; message?: string };
      const msg = errorObj?.data?.error || errorObj?.message || 'Failed to post reply';
      toast.error('Reply failed', { description: msg });
    } finally {
      setUploadingFiles(false);
    }
  };

  // Status badge styling
  const getStatusBadge = (status: SupportTicketStatus) => {
    switch (status) {
      case 'NEW':
        return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 border border-blue-200">New</span>;
      case 'IN_PROGRESS':
        return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800 border border-indigo-200">In Progress</span>;
      case 'WAITING_FOR_USER':
        return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800 border border-amber-200">Waiting for You</span>;
      case 'WAITING_INTERNAL':
        return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 border border-purple-200">Under Review</span>;
      case 'RESOLVED':
        return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">Resolved</span>;
      case 'CLOSED':
        return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-700 border border-gray-300">Closed</span>;
      default:
        return <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getPriorityBadge = (p: SupportPriority | null | undefined) => {
    switch (p) {
      case 'CRITICAL':
        return <span className="px-2 py-0.5 text-xs font-bold rounded bg-red-100 text-red-700 border border-red-200">Critical</span>;
      case 'HIGH':
        return <span className="px-2 py-0.5 text-xs font-semibold rounded bg-orange-100 text-orange-700 border border-orange-200">High</span>;
      case 'MEDIUM':
        return <span className="px-2 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-800 border border-yellow-200">Medium</span>;
      case 'LOW':
        return <span className="px-2 py-0.5 text-xs font-normal rounded bg-green-100 text-green-700 border border-green-200">Low</span>;
      default:
        return <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-600 border border-gray-200">Pending Triage</span>;
    }
  };

  const getQueryTypeBadge = (qt: SupportQueryType) => {
    if (qt === 'TECHNICAL_SUPPORT') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded bg-purple-100 text-purple-800 border border-purple-200">
          <Shield className="w-3 h-3 text-purple-600" />
          Technical Support
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded bg-blue-100 text-blue-800 border border-blue-200">
        <Layers className="w-3 h-3 text-blue-600" />
        Internal Issue
      </span>
    );
  };

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="w-8 h-8 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isGuest) {
    if (publicSuccessData) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            {/* Success Icon */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl mb-4">
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-gray-900 mb-2 font-bold text-2xl">Message Sent Successfully!</h1>
              <p className="text-gray-600">
                We&apos;ve received your support request
              </p>
            </div>

            {/* Success Card */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-6">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg mb-6">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-green-900 mb-1">
                      <strong>Support Ticket Created</strong>
                    </p>
                    <p className="text-xs text-green-700">
                      Ticket ID: <strong>#{publicSuccessData.ticketCode}</strong>
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Subject</p>
                  <p className="text-sm text-gray-900">{publicSuccessData.subject}</p>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Category</p>
                  <p className="text-sm text-gray-900">{publicSuccessData.category}</p>
                </div>
              </div>

              <h3 className="text-gray-900 text-sm font-semibold mb-4">What happens next?</h3>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-blue-600 text-xs font-semibold">1</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-900 font-medium">Confirmation Email</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      You&apos;ll receive a confirmation at {publicSuccessData.email}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-blue-600 text-xs font-semibold">2</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-900 font-medium">Team Review</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Our support team will review your request
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                    <span className="text-blue-600 text-xs font-semibold">3</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-900 font-medium">Get Response</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      We&apos;ll respond within 24 hours on business days
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setPublicSuccessData(null);
                  setPublicFullName('');
                  setPublicEmail('');
                  setPublicPhone('');
                  setPublicSubject('');
                  setPublicMessage('');
                  setPublicErrors({});
                }}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 mb-3 cursor-pointer font-medium"
              >
                <span>Send Another Message</span>
              </button>

              {onBack && (
                <button
                  type="button"
                  onClick={onBack}
                  className="w-full py-3 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2 cursor-pointer font-medium"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Back to Previous Page</span>
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl mb-4 shadow-lg shadow-blue-500/20">
              <Headphones className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Contact Support</h1>
            <p className="text-gray-600">
              We&apos;re here to help! Get in touch with our support team
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Send us a message</h2>

                <form onSubmit={handlePublicSubmit} className="space-y-4">
                  {/* Name */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-1.5">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={publicFullName}
                      onChange={(e) => {
                        setPublicFullName(e.target.value);
                        if (publicErrors.fullName) setPublicErrors((prev) => ({ ...prev, fullName: '' }));
                      }}
                      className={`w-full px-4 py-2.5 bg-white border ${
                        publicErrors.fullName ? 'border-red-300' : 'border-gray-200'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                      placeholder="John Doe"
                    />
                    {publicErrors.fullName && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {publicErrors.fullName}
                      </p>
                    )}
                  </div>

                  {/* Email & Phone */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1.5">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={publicEmail}
                        onChange={(e) => {
                          setPublicEmail(e.target.value);
                          if (publicErrors.email) setPublicErrors((prev) => ({ ...prev, email: '' }));
                        }}
                        className={`w-full px-4 py-2.5 bg-white border ${
                          publicErrors.email ? 'border-red-300' : 'border-gray-200'
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                        placeholder="john@company.com"
                      />
                      {publicErrors.email && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {publicErrors.email}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm text-gray-700 mb-1.5">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={publicPhone}
                        onChange={(e) => {
                          setPublicPhone(e.target.value);
                          if (publicErrors.phone) setPublicErrors((prev) => ({ ...prev, phone: '' }));
                        }}
                        className={`w-full px-4 py-2.5 bg-white border ${
                          publicErrors.phone ? 'border-red-300' : 'border-gray-200'
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                        placeholder="+91 98765 43210"
                      />
                      {publicErrors.phone && (
                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5" />
                          {publicErrors.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-1.5">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="category"
                      value={publicCategory}
                      onChange={(e) => {
                        setPublicCategory(e.target.value);
                        if (publicErrors.category) setPublicErrors((prev) => ({ ...prev, category: '' }));
                      }}
                      className={`w-full px-4 py-2.5 bg-white border ${
                        publicErrors.category ? 'border-red-300' : 'border-gray-200'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    >
                      <option value="">Select a category</option>
                      {SUPPORT_PUBLIC_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                    {publicErrors.category && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {publicErrors.category}
                      </p>
                    )}
                  </div>

                  {/* Subject */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-1.5">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="subject"
                      value={publicSubject}
                      onChange={(e) => {
                        setPublicSubject(e.target.value);
                        if (publicErrors.subject) setPublicErrors((prev) => ({ ...prev, subject: '' }));
                      }}
                      className={`w-full px-4 py-2.5 bg-white border ${
                        publicErrors.subject ? 'border-red-300' : 'border-gray-200'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                      placeholder="Brief description of your issue"
                    />
                    {publicErrors.subject && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {publicErrors.subject}
                      </p>
                    )}
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-1.5">
                      Message <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={publicMessage}
                      onChange={(e) => {
                        setPublicMessage(e.target.value);
                        if (publicErrors.message) setPublicErrors((prev) => ({ ...prev, message: '' }));
                      }}
                      rows={6}
                      className={`w-full px-4 py-2.5 bg-white border ${
                        publicErrors.message ? 'border-red-300' : 'border-gray-200'
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none`}
                      placeholder="Please provide as much detail as possible..."
                    ></textarea>
                    {publicErrors.message && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5" />
                        {publicErrors.message}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      {publicMessage.length} characters (minimum 10)
                    </p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={createPublicTicketMutation.isPending}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2 cursor-pointer font-medium"
                  >
                    {createPublicTicketMutation.isPending ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>

                  {onBack && (
                    <button
                      type="button"
                      onClick={onBack}
                      className="w-full py-3 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-2 cursor-pointer font-medium"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      <span>Back</span>
                    </button>
                  )}
                </form>
              </div>
            </div>

            {/* Contact Information */}
            <div className="lg:col-span-1 space-y-6">
              {/* Quick Contact */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <h3 className="text-gray-900 font-semibold mb-4">Quick Contact</h3>

                <div className="space-y-4">
                  {/* Email */}
                  <a
                    href={`mailto:${supportContact.email}`}
                    className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Email</p>
                      <p className="text-xs text-gray-600">{supportContact.email}</p>
                    </div>
                  </a>

                  {/* Phone */}
                  <a
                    href={`tel:${supportContact.phone.replace(/\s+/g, '')}`}
                    className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                  >
                    <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">Phone</p>
                      <p className="text-xs text-gray-600">{supportContact.phone}</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Office Hours */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <h3 className="text-gray-900 font-semibold">Support Hours</h3>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monday - Friday</span>
                    <span className="text-gray-900 font-medium">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Saturday</span>
                    <span className="text-gray-900 font-medium">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sunday</span>
                    <span className="text-gray-900 font-medium">Closed</span>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">
                  All times in IST (Indian Standard Time)
                </p>
              </div>

              {/* FAQ Link */}
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-purple-200 rounded-2xl p-6">
                <div className="flex items-center gap-2 mb-3">
                  <HelpCircle className="w-5 h-5 text-blue-600" />
                  <h3 className="text-gray-900 font-semibold">Common Questions</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Find quick answers in our FAQ section before contacting support.
                </p>
                <a
                  href="/support/faqs"
                  onClick={(e) => {
                    e.preventDefault();
                    onBrowseFAQs?.();
                  }}
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors font-medium"
                >
                  <FileText className="w-4 h-4" />
                  <span>Browse FAQs</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =========================================================================
  // AUTHENTICATED TENANT SUPPORT EXPERIENCE
  // =========================================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/40 via-white to-slate-50/50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header with Hierarchy and Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl shadow-xs border border-gray-200/80">
          <div className="flex items-center gap-3.5">
            <div className="w-11 h-11 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-xs shadow-blue-500/10 text-white shrink-0">
              <Headphones className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 tracking-tight">MEPIfy Support Center</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Get help with MEPIfy workflows, report an issue, or track an existing request.
              </p>
            </div>
          </div>

          {/* Area Tabs: [ + New Ticket ] [ My Tickets (count) ] */}
          <div className="flex items-center gap-1.5 bg-gray-100/80 p-1 rounded-xl border border-gray-200/70 shrink-0">
            <button
              type="button"
              onClick={() => setActiveTab('new')}
              className={`flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                activeTab === 'new'
                  ? 'bg-white text-blue-700 shadow-xs border border-gray-200/60 font-bold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
              }`}
            >
              <PlusCircle className="w-3.5 h-3.5 text-blue-600" />
              New Ticket
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('tickets')}
              className={`flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                activeTab === 'tickets'
                  ? 'bg-white text-blue-700 shadow-xs border border-gray-200/60 font-bold'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
              }`}
            >
              <MessageSquare className="w-3.5 h-3.5 text-indigo-600" />
              <span>My Tickets</span>
              {tenantTicketsData?.pagination?.total ? (
                <span
                  className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    activeTab === 'tickets'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {tenantTicketsData.pagination.total}
                </span>
              ) : null}
            </button>
          </div>
        </div>

        {activeTab === 'new' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Left / Primary Column: Create Support Ticket */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-6 sm:p-7 space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Create Support Ticket</h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Provide accurate details below so our operations support desk can resolve your request efficiently.
                  </p>
                </div>

                {recentlyCreatedCode && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-semibold text-emerald-900">Ticket Created Successfully</h4>
                      <p className="text-xs text-emerald-700 mt-0.5">
                        Your reference ticket code is <span className="font-mono font-bold">{recentlyCreatedCode}</span>.
                        You can track progress in the{' '}
                        <button
                          type="button"
                          onClick={() => setActiveTab('tickets')}
                          className="underline font-semibold cursor-pointer"
                        >
                          My Tickets
                        </button>{' '}
                        tab.
                      </p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleCreateSubmit} className="space-y-5">
                  {/* 1. Category */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Category <span className="text-red-500 text-xs ml-0.5">*</span>
                    </label>
                    <select
                      value={category}
                      onChange={(e) => {
                        setCategory(e.target.value);
                        if (formErrors.category) setFormErrors((p) => ({ ...p, category: '' }));
                      }}
                      className={`w-full px-3.5 py-2.5 bg-white border rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer ${
                        formErrors.category ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-200'
                      }`}
                    >
                      <option value="" disabled>Select a category...</option>
                      <option value="General Inquiry">General Inquiry</option>
                      <optgroup label="Authentication & Access">
                        <option value="Sign-In & Authentication Issues">Sign-In & Authentication Issues</option>
                        <option value="Sign-Up / Onboarding Issues">Sign-Up / Onboarding Issues</option>
                        <option value="Access Permission Issues">Access Permission Issues</option>
                      </optgroup>
                      <optgroup label="Operational Workflow Support">
                        <option value="Submission Workflow Issues">Submission Workflow Issues</option>
                        <option value="Documentation & Policy Issues">Documentation & Policy Issues</option>
                        <option value="Equipment Testing Issues">Equipment Testing Issues</option>
                        <option value="QA / Compliance Checklist Gaps">QA / Compliance Checklist Gaps</option>
                      </optgroup>
                      <optgroup label="Technical Platform Support">
                        <option value="Dashboard / UI Errors">Dashboard / UI Errors</option>
                        <option value="API / Integration Failures">API / Integration Failures</option>
                        <option value="Notification / Broadcast Delivery Failures">Notification / Broadcast Delivery Failures</option>
                      </optgroup>
                      <optgroup label="Feedback & Requests">
                        <option value="Feature Requests">Feature Requests</option>
                      </optgroup>
                    </select>
                    {formErrors.category && (
                      <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                        {formErrors.category}
                      </p>
                    )}
                  </div>

                  {/* 2. Contract & Project Site (Side by side) */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                        Related Contract <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <select
                        value={contractId}
                        onChange={(e) => handleContractChange(e.target.value)}
                        className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer"
                      >
                        <option value="">None / General Organization</option>
                        {availableContracts.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.contractNumber ? `[${c.contractNumber}] ` : ''}{c.contractName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="block text-xs font-semibold text-gray-700">
                          Related Project Site <span className="text-gray-400 font-normal">(Optional)</span>
                        </label>
                        {contractId && (
                          <span className="text-[11px] text-blue-600 font-medium bg-blue-50 px-1.5 py-0.5 rounded">
                            Filtered by contract
                          </span>
                        )}
                      </div>
                      <select
                        disabled={!contractId}
                        value={projectSiteId}
                        onChange={(e) => setProjectSiteId(e.target.value)}
                        className="w-full px-3.5 py-2.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                      >
                        {!contractId ? (
                          <option value="">Select a contract first</option>
                        ) : (
                          <>
                            <option value="">None / General Site</option>
                            {availableSites.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.siteCode ? `[${s.siteCode}] ` : ''}{s.siteName}
                              </option>
                            ))}
                            {availableSites.length === 0 && (
                              <option value="" disabled>No project sites associated with this contract</option>
                            )}
                          </>
                        )}
                      </select>
                    </div>
                  </div>

                  {/* 3. Subject */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Subject <span className="text-red-500 text-xs ml-0.5">*</span>
                    </label>
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => {
                        setSubject(e.target.value);
                        if (formErrors.subject) setFormErrors((p) => ({ ...p, subject: '' }));
                      }}
                      placeholder="Brief summary of your issue or request..."
                      className={`w-full px-3.5 py-2.5 bg-white border rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${
                        formErrors.subject ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-200'
                      }`}
                    />
                    {formErrors.subject && (
                      <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                        {formErrors.subject}
                      </p>
                    )}
                  </div>

                  {/* 4. Description */}
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                      Description <span className="text-red-500 text-xs ml-0.5">*</span>
                    </label>
                    <textarea
                      rows={6}
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value);
                        if (formErrors.description) setFormErrors((p) => ({ ...p, description: '' }));
                      }}
                      placeholder="Please describe the issue in detail, including steps to reproduce or relevant context..."
                      className={`w-full px-3.5 py-2.5 bg-white border rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-none ${
                        formErrors.description ? 'border-red-300 ring-1 ring-red-300' : 'border-gray-200'
                      }`}
                    />
                    <div className="flex justify-between items-center mt-1">
                      {formErrors.description ? (
                        <p className="text-xs text-red-600 flex items-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                          {formErrors.description}
                        </p>
                      ) : (
                        <span className="text-xs text-gray-400">Minimum 5 characters</span>
                      )}
                      <span className="text-xs text-gray-400 font-mono">{description.length} chars</span>
                    </div>
                  </div>

                  {/* 5. Submit Actions */}
                  <div className="pt-2 flex items-center gap-3 border-t border-gray-100">
                    <button
                      type="submit"
                      disabled={createTicketMutation.isPending}
                      className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 font-semibold text-xs shadow-xs shadow-blue-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2 cursor-pointer"
                    >
                      {createTicketMutation.isPending ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Submitting Ticket...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Submit Ticket
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelCreate}
                      disabled={createTicketMutation.isPending}
                      className="px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Right / Secondary Column: Direct Support Channels, Operating Schedule, Knowledge Base */}
            <div className="lg:col-span-1 space-y-5">
              {/* Card 1: Direct Support Channels */}
              <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-5 space-y-3.5">
                <h3 className="text-sm font-bold text-gray-900">Direct Support Channels</h3>
                <div className="space-y-2.5">
                  <a
                    href={`mailto:${supportContact.email}`}
                    className="flex items-center gap-3 p-3 bg-blue-50/60 hover:bg-blue-100/70 rounded-xl transition-colors border border-blue-100/80 group"
                  >
                    <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shrink-0 shadow-xs text-white group-hover:scale-105 transition-transform">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden min-w-0">
                      <p className="text-xs font-bold text-gray-900">Email Operations</p>
                      <p className="text-xs text-gray-600 truncate">{supportContact.email}</p>
                    </div>
                  </a>

                  <a
                    href={`tel:${supportContact.phone.replace(/\s+/g, '')}`}
                    className="flex items-center gap-3 p-3 bg-indigo-50/60 hover:bg-indigo-100/70 rounded-xl transition-colors border border-indigo-100/80 group"
                  >
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center shrink-0 shadow-xs text-white group-hover:scale-105 transition-transform">
                      <Phone className="w-4 h-4" />
                    </div>
                    <div className="overflow-hidden min-w-0">
                      <p className="text-xs font-bold text-gray-900">Phone Support</p>
                      <p className="text-xs text-gray-600">{supportContact.phone}</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* Card 2: Operating Schedule */}
              <div className="bg-white rounded-2xl shadow-xs border border-gray-200/80 p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <h3 className="text-sm font-bold text-gray-900">Operating Schedule</h3>
                </div>
                <div className="space-y-2 text-xs text-gray-600">
                  <div className="flex justify-between items-center py-0.5">
                    <span>Monday - Friday</span>
                    <span className="font-semibold text-gray-900">9:00 AM - 6:00 PM IST</span>
                  </div>
                  <div className="flex justify-between items-center py-0.5">
                    <span>Saturday</span>
                    <span className="font-semibold text-gray-900">10:00 AM - 4:00 PM IST</span>
                  </div>
                  <div className="flex justify-between items-center py-0.5">
                    <span>Sunday</span>
                    <span className="font-semibold text-gray-400">Closed (On-Call SLA Only)</span>
                  </div>
                </div>
                <p className="text-[11px] text-gray-400 pt-2 border-t border-gray-100">
                  All times in IST (Indian Standard Time)
                </p>
              </div>

              {/* Card 3: Knowledge Base */}
              <div className="bg-gradient-to-br from-blue-50/60 to-indigo-50/60 border border-blue-200/70 rounded-2xl p-5 space-y-2.5">
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-blue-600" />
                  <h3 className="text-sm font-bold text-gray-900">Knowledge Base</h3>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Have a question? Check our answers to standard MEPIfy workflows before submitting a request.
                </p>
                <button
                  type="button"
                  onClick={() => onBrowseFAQs?.()}
                  className="text-xs font-semibold text-blue-700 hover:text-blue-900 flex items-center gap-1.5 pt-1 cursor-pointer"
                >
                  <FileText className="w-3.5 h-3.5" />
                  Browse FAQs & Guidance
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: MY TICKETS (REAL API INTEGRATION)                                  */}
        {/* ========================================================================= */}
        {activeTab === 'tickets' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 bg-white rounded-2xl shadow-sm border border-gray-200/80 overflow-hidden min-h-[600px]">
            {/* ------------------------------------------------------------- */}
            {/* Left Queue Panel: Ticket List (col-span-5)                    */}
            {/* ------------------------------------------------------------- */}
            <div className="lg:col-span-5 border-r border-gray-200/80 flex flex-col h-full bg-gray-50/40">
              {/* Filter & Search Header */}
              <div className="p-4 border-b border-gray-200/80 bg-white space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                    My Support Requests
                  </span>
                  <button
                    onClick={() => refetchTickets()}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded"
                    title="Refresh tickets"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-2.5 h-3.5 w-3.5 text-gray-400" />
                  <input
                    type="text"
                    value={ticketSearch}
                    onChange={(e) => setTicketSearch(e.target.value)}
                    placeholder="Search by subject or ticket code..."
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg pl-9 pr-3 py-2 text-xs text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                {/* Status Micro-tabs */}
                <div className="flex gap-1 overflow-x-auto pb-0.5">
                  {['ALL', 'NEW', 'IN_PROGRESS', 'WAITING_FOR_USER', 'RESOLVED', 'CLOSED'].map((st) => (
                    <button
                      key={st}
                      onClick={() => setStatusFilter(st)}
                      className={`text-[10px] font-semibold px-2 py-1 rounded-md transition-all shrink-0 ${
                        statusFilter === st
                          ? 'bg-blue-600 text-white shadow-xs'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {st === 'ALL' ? 'All' : st.replace(/_/g, ' ')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tickets Scrolling List */}
              <div className="flex-1 overflow-y-auto divide-y divide-gray-100 custom-scrollbar">
                {loadingTickets ? (
                  <div className="p-8 text-center space-y-3">
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-xs text-gray-500">Loading your support tickets...</p>
                  </div>
                ) : ticketsError ? (
                  <div className="p-8 text-center space-y-2">
                    <AlertCircle className="w-6 h-6 text-red-500 mx-auto" />
                    <p className="text-xs text-gray-700 font-semibold">Failed to load tickets</p>
                    <button
                      onClick={() => refetchTickets()}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Try again
                    </button>
                  </div>
                ) : !tenantTicketsData?.tickets?.length ? (
                  <div className="p-8 text-center space-y-3">
                    <MessageSquare className="w-8 h-8 text-gray-300 mx-auto" />
                    <p className="text-xs font-semibold text-gray-700">No support tickets found</p>
                    <p className="text-[11px] text-gray-400">
                      You haven&apos;t submitted any tickets matching the current filters.
                    </p>
                    <button
                      onClick={() => setActiveTab('new')}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors"
                    >
                      <PlusCircle className="w-3 h-3" />
                      Create a Ticket
                    </button>
                  </div>
                ) : (
                  tenantTicketsData.tickets.map((ticket) => {
                    const isSelected = selectedTicketId === ticket.id;
                    return (
                      <div
                        key={ticket.id}
                        onClick={() => setSelectedTicketId(ticket.id)}
                        className={`p-4 cursor-pointer transition-all border-l-4 ${
                          isSelected
                            ? 'bg-blue-50/60 border-blue-600'
                            : 'bg-white hover:bg-gray-50 border-transparent'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <span className="font-mono text-xs font-bold text-gray-900">
                            {ticket.ticketCode}
                          </span>
                          {getStatusBadge(ticket.status)}
                        </div>

                        <h4 className="text-xs font-semibold text-gray-900 line-clamp-1 mb-2">
                          {ticket.subject}
                        </h4>

                        <div className="flex flex-wrap items-center gap-1.5 mb-2">
                          {getQueryTypeBadge(ticket.queryType)}
                          {getPriorityBadge(ticket.priority)}
                        </div>

                        <div className="flex items-center justify-between text-[10px] text-gray-400">
                          <span>
                            Created: {new Date(ticket.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })}
                          </span>
                          <ChevronRight className="w-3 h-3 text-gray-300" />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* ------------------------------------------------------------- */}
            {/* Right Conversation & Details Panel (col-span-7)               */}
            {/* ------------------------------------------------------------- */}
            <div className="lg:col-span-7 flex flex-col h-full bg-white">
              {!selectedTicketId ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-gray-400">
                  <Headphones className="w-10 h-10 text-gray-200 mb-3" />
                  <p className="text-sm font-semibold text-gray-700">Select a Ticket</p>
                  <p className="text-xs text-gray-400 max-w-xs mt-1">
                    Choose a ticket from the left queue to view chronological conversation messages and post replies.
                  </p>
                </div>
              ) : loadingTicketDetail ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                  <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-3" />
                  <p className="text-xs text-gray-500">Loading conversation history...</p>
                </div>
              ) : ticketDetailError || !activeTicketDetail ? (
                <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                  <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
                  <p className="text-xs font-semibold text-gray-800">Failed to load ticket detail</p>
                  <p className="text-[11px] text-gray-500 mt-1">Ticket may not exist or you lack permission to view it.</p>
                  <button
                    onClick={() => refetchTicketDetail()}
                    className="mt-3 text-xs text-blue-600 hover:underline"
                  >
                    Retry
                  </button>
                </div>
              ) : (
                <div className="flex flex-col h-full">
                  {/* Ticket Header & Context Details */}
                  <div className="p-5 border-b border-gray-200/80 bg-gray-50/50 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold text-gray-900">
                          {activeTicketDetail.ticketCode}
                        </span>
                        {getStatusBadge(activeTicketDetail.status)}
                      </div>
                      <div className="flex items-center gap-2">
                        {getQueryTypeBadge(activeTicketDetail.queryType)}
                        {getPriorityBadge(activeTicketDetail.priority)}
                      </div>
                    </div>

                    <h2 className="text-base font-bold text-gray-900">
                      {activeTicketDetail.subject}
                    </h2>

                    {/* Metadata chips */}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                      {activeTicketDetail.contractId && (
                        <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded border border-gray-200">
                          <Building className="w-3 h-3 text-gray-400" />
                          Contract Linked
                        </span>
                      )}
                      {activeTicketDetail.projectSiteId && (
                        <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded border border-gray-200">
                          <MapPin className="w-3 h-3 text-gray-400" />
                          Site Linked
                        </span>
                      )}
                      <span className="text-gray-400">
                        Updated {new Date(activeTicketDetail.updatedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>

                  {/* Chronological Public Conversation */}
                  <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-gray-50/20 custom-scrollbar">
                    {activeTicketDetail.messages?.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-6">No messages found.</p>
                    ) : (
                      activeTicketDetail.messages.map((msg) => {
                        const isRequester = msg.senderRole === 'REQUESTER' || msg.senderRole === 'User' || msg.senderRole === 'TENANT_STAFF';
                        return (
                          <div
                            key={msg.id}
                            className={`flex flex-col max-w-[85%] ${
                              isRequester ? 'ml-auto items-end' : 'mr-auto items-start'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1 text-[11px] text-gray-400">
                              <span className="font-semibold text-gray-700">
                                {msg.senderName || (isRequester ? 'You' : 'Support Specialist')}
                              </span>
                              <span>•</span>
                              <span>
                                {new Date(msg.createdAt).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                            </div>

                            <div
                              className={`p-3.5 rounded-2xl text-xs leading-relaxed ${
                                isRequester
                                  ? 'bg-blue-600 text-white rounded-br-xs shadow-sm'
                                  : 'bg-white text-gray-800 rounded-bl-xs border border-gray-200 shadow-sm'
                              }`}
                            >
                              <p className="whitespace-pre-wrap">{msg.body}</p>

                              {msg.attachments && msg.attachments.length > 0 && (
                                <div className="mt-2.5 pt-2 border-t border-current/20 space-y-1.5">
                                  <div className="text-[10px] font-semibold opacity-80 flex items-center gap-1">
                                    <Paperclip className="w-3 h-3" /> Attachments ({msg.attachments.length})
                                  </div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {msg.attachments.map((att) => (
                                      <button
                                        key={att.id}
                                        type="button"
                                        onClick={() => downloadTenantSupportAttachment(activeTicketDetail.id, att.id)}
                                        className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded border font-medium transition-colors ${
                                          isRequester
                                            ? 'bg-blue-700 hover:bg-blue-800 text-white border-blue-500/60'
                                            : 'bg-gray-100 hover:bg-gray-200 text-gray-800 border-gray-300'
                                        }`}
                                      >
                                        <Download className="w-3 h-3" />
                                        <span className="truncate max-w-[140px]">{att.name}</span>
                                        <span className="text-[9px] opacity-75">
                                          ({Math.round(att.size / 1024)} KB)
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Reply Composer or Closed State */}
                  <div className="p-4 border-t border-gray-200 bg-white">
                    {activeTicketDetail.status === 'CLOSED' ? (
                      <div className="p-3 bg-gray-100 border border-gray-200 rounded-xl text-center">
                        <p className="text-xs font-semibold text-gray-700">
                          This ticket is closed.
                        </p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Replies are disabled. If you require further assistance, please submit a new ticket.
                        </p>
                      </div>
                    ) : (
                      <form onSubmit={handleSendReply} className="space-y-2">
                        {/* Selected Attachments Preview */}
                        {selectedFiles.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 p-2 bg-gray-50 border border-gray-200 rounded-lg">
                            {selectedFiles.map((file, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center gap-1 text-[11px] bg-white border border-gray-300 px-2 py-0.5 rounded text-gray-700 font-medium"
                              >
                                <Paperclip className="w-3 h-3 text-gray-500" />
                                <span className="truncate max-w-[120px]">{file.name}</span>
                                <span className="text-[9px] text-gray-400">({Math.round(file.size / 1024)}KB)</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveFile(idx)}
                                  className="text-gray-400 hover:text-red-500 ml-0.5 cursor-pointer"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="relative">
                          <textarea
                            rows={3}
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Type your reply to the support team..."
                            className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-xs text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileSelect}
                              className="hidden"
                              multiple
                            />
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center gap-1 text-xs cursor-pointer"
                              title="Attach file (Max 10MB)"
                            >
                              <Paperclip className="w-3.5 h-3.5" />
                              <span className="text-[11px]">Attach</span>
                            </button>
                            <span className="text-[10px] text-gray-400">
                              Public reply to operations help desk
                            </span>
                          </div>
                          <button
                            type="submit"
                            disabled={addMessageMutation.isPending || uploadingFiles || (!replyText.trim() && selectedFiles.length === 0)}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 cursor-pointer"
                          >
                            {addMessageMutation.isPending || uploadingFiles ? (
                              <>
                                <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                {uploadingFiles ? 'Uploading...' : 'Sending...'}
                              </>
                            ) : (
                              <>
                                <Send className="w-3.5 h-3.5" />
                                Send Reply
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}